import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  type ComponentProps,
  type FormEvent,
  type ReactNode,
} from 'react';
import { cn } from '../lib/utils';
import { Badge } from './Badge';
import { Spinner } from './ui/spinner';

export type QuestionnaireQuestion = {
  name: string;
  prompt: string;
  description?: string;
  required?: boolean;
  multiple?: boolean;
  choices: { value: string; label: string; description?: string; recommended?: boolean }[];
};

type QuestionnaireFormProps = {
  questions: QuestionnaireQuestion[];
  onComplete: (answers: string[][]) => void;
  className?: string;
  /** Shown at the end of each question's title line, such as where the question sits in a flow. */
  aside?: ReactNode;
};

const QuestionnaireForm = lazy(async () => {
  const {
    Questionnaire,
    QuestionnaireProgress,
    QuestionnaireItem,
    QuestionnaireTitle,
    QuestionnaireDescription,
    QuestionnaireChoices,
    QuestionnaireChoice,
    QuestionnaireError,
    QuestionnaireActions,
    QuestionnairePrevious,
    QuestionnaireSkip,
    QuestionnaireNext,
    QuestionnaireSubmit,
  } = await import('./ui/questionnaire');

  function Item(props: ComponentProps<typeof QuestionnaireItem>) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const item = ref.current?.querySelector('fieldset');
      if (item instanceof HTMLElement) item.focus();
    }, []);

    return (
      <div className="contents" ref={ref}>
        <QuestionnaireItem {...props} />
      </div>
    );
  }

  function Form({ questions, onComplete, className, aside }: QuestionnaireFormProps) {
    const submit = (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const answers = questions.map((question) =>
        data.getAll(question.name).map(String).filter(Boolean),
      );
      onComplete(answers);
    };

    return (
      <Questionnaire
        className={cn(
          'w-[26rem] max-w-full gap-2 rounded-lg border border-border bg-background-surface p-3 text-sm outline-none [&_[hidden]]:!hidden',
          className,
        )}
        items={questions.map((question) => ({
          name: question.name,
          required: question.required ?? true,
          choices: question.choices.map((choice) => ({ value: choice.value })),
        }))}
        shortcuts="numbers"
        onSubmit={submit}
        // A click on the card's text would leave focus on the page, out of reach of the shortcuts
        // and arrow keys. The form takes that focus and hands it to the open question.
        tabIndex={-1}
        onFocus={(event) => {
          if (event.target !== event.currentTarget) return;
          event.currentTarget.querySelector<HTMLElement>('fieldset:not([hidden])')?.focus();
        }}
      >
        {questions.length > 1 && (
          <QuestionnaireProgress className="text-xs text-muted-foreground" />
        )}
        {questions.map((question, index) => (
          <Item
            key={question.name}
            multiple={question.multiple}
            name={question.name}
            required={question.required ?? true}
          >
            <QuestionnaireTitle className="mb-1 flex w-full items-baseline gap-3 text-sm font-medium">
              <span className="flex-1">
                {index + 1}. {question.prompt}
              </span>
              {aside}
            </QuestionnaireTitle>
            {question.description && (
              <QuestionnaireDescription className="text-xs text-muted-foreground">
                {question.description}
              </QuestionnaireDescription>
            )}
            <QuestionnaireChoices className="gap-0.5">
              {question.choices.map((choice) => (
                <QuestionnaireChoice
                  className="relative min-h-0 border-0 bg-transparent px-1 py-1 hover:bg-background-muted data-[checked]:bg-background-elevated data-[checked]:ring-1 data-[checked]:ring-foreground-accent [&_[data-slot=questionnaire-choice-indicator]]:hidden data-[checked]:[&_[data-slot=questionnaire-choice-shortcut]]:border-foreground-accent data-[checked]:[&_[data-slot=questionnaire-choice-shortcut]]:text-foreground-accent"
                  key={choice.value}
                  value={choice.value}
                >
                  <span>
                    {choice.label}
                    {choice.recommended && (
                      <Badge className="ms-2" size="compact" variant="primary">
                        Recommended
                      </Badge>
                    )}
                  </span>
                  {choice.description && (
                    <span className="text-xs text-muted-foreground">{choice.description}</span>
                  )}
                </QuestionnaireChoice>
              ))}
            </QuestionnaireChoices>
            <QuestionnaireError />
          </Item>
        ))}
        <QuestionnaireActions className="mt-1 flex justify-end gap-2">
          <QuestionnairePrevious className="mr-auto" size="sm" variant="ghost" />
          <QuestionnaireSkip size="sm" variant="ghost">
            Skip
          </QuestionnaireSkip>
          <QuestionnaireNext size="sm" variant="default">
            Continue
          </QuestionnaireNext>
          <QuestionnaireSubmit size="sm" variant="default">
            Continue
          </QuestionnaireSubmit>
        </QuestionnaireActions>
      </Questionnaire>
    );
  }

  return { default: Form };
});

/** Letter shortcuts, one question at a time. The shadcn package loads only when this mounts. */
export function Questionnaire(props: QuestionnaireFormProps) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center p-3">
          <Spinner />
        </div>
      }
    >
      <QuestionnaireForm {...props} />
    </Suspense>
  );
}
